import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Package, ShieldCheck, Calendar, Wrench, MapPin } from "@phosphor-icons/react";

export default function PublicItem() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/inventory/${id}`);
        setItem(data);
      } catch (e) { setErr(formatApiError(e)); }
    })();
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-950 p-6 text-zinc-400">
        <div className="max-w-md text-center space-y-3">
          <Logo size={40} />
          <div className="text-xl font-bold text-zinc-100 mt-4">Asset not found</div>
          <p className="text-sm text-zinc-500">{err}</p>
        </div>
      </div>
    );
  }
  if (!item) return <div className="min-h-screen grid place-items-center bg-zinc-950 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="public-item-page">
      <header className="border-b border-zinc-900 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <Logo size={36} />
          <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-emerald-400">
            <ShieldCheck size={12} weight="duotone" /> Verified Asset
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 md:p-8 space-y-6">
        <div className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 flex gap-5 items-start">
          <div className="h-20 w-20 rounded-md bg-zinc-800 overflow-hidden grid place-items-center flex-shrink-0">
            {item.photo
              ? <img src={item.photo} alt={item.name} className="h-full w-full object-cover" />
              : <Package size={30} className="text-zinc-600" weight="duotone" />}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">{item.category}</div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              {item.name}
            </h1>
            <div className="text-sm font-mono text-zinc-400 mt-1">{item.sku}</div>
          </div>
        </div>

        <section className="grid grid-cols-2 gap-3">
          <Info icon={MapPin} label="Location">{item.location}{item.floor ? ` · ${item.floor}` : ""}</Info>
          <Info icon={Package} label="Serial">{item.serial_number || "—"}</Info>
          <Info icon={Package} label="IP Address">{item.ip_address || "—"}</Info>
          <Info icon={Calendar} label="Registered">{new Date(item.created_at).toLocaleDateString()}</Info>
        </section>

        <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Purchase Information</div>
          <div className="grid grid-cols-2 gap-4">
            <KV label="Purchase Date" value={item.purchase_date ? new Date(item.purchase_date).toLocaleDateString() : "—"} />
            <KV label="Price" value={item.purchase_price != null ? `$${Number(item.purchase_price).toFixed(2)}` : "—"} />
            <KV label="Vendor" value={item.purchase_vendor || "—"} />
            <KV label="End of Life" value={item.end_of_life ? new Date(item.end_of_life).toLocaleDateString() : "—"} />
          </div>
        </section>

        <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-2 mb-4">
            <Wrench size={14} className="text-[#F5A623]" />
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Maintenance</div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <KV label="Last Service" value={item.last_maintenance ? new Date(item.last_maintenance).toLocaleDateString() : "—"} />
            <KV label="Next Service" value={item.next_maintenance ? new Date(item.next_maintenance).toLocaleDateString() : "—"} highlight={!!item.next_maintenance} />
          </div>
          <div className="divide-y divide-zinc-800/60">
            {(item.maintenance_log || []).slice().reverse().map((m, idx) => (
              <div key={idx} className="py-2 text-sm flex justify-between">
                <span className="text-zinc-300">{m.notes || "Scheduled maintenance"}</span>
                <span className="font-mono text-xs text-zinc-500">{new Date(m.date).toLocaleDateString()}</span>
              </div>
            ))}
            {(!item.maintenance_log || item.maintenance_log.length === 0) && (
              <div className="text-xs text-zinc-600">No past maintenance records.</div>
            )}
          </div>
        </section>

        {item.notes && (
          <section className="p-6 rounded-lg bg-zinc-900/50 border border-zinc-800">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Notes</div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{item.notes}</p>
          </section>
        )}

        <footer className="text-center text-[10px] tracking-[0.3em] uppercase text-zinc-600 py-6">
          KERMA GAMES · Asset {item.sku}
        </footer>
      </main>
    </div>
  );
}

function Info({ icon: Icon, label, children }) {
  return (
    <div className="p-4 rounded-lg bg-zinc-900/50 border border-zinc-800">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-zinc-500">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1.5 text-sm text-zinc-100">{children}</div>
    </div>
  );
}
function KV({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
      <div className={`mt-1 text-sm font-mono ${highlight ? "text-[#F5A623]" : "text-zinc-100"}`}>{value}</div>
    </div>
  );
}
